import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
  useTheme,
} from "@mui/material";
import I18n from "i18n-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface SubmissionSuccessModalProps {
  open: boolean;
  onClose: () => void;
  onFillAnother: () => void;
  hasMoreWaivers: boolean;
  token?: string;
  countdownSeconds?: number;
}

export default function SubmissionSuccessModal({
  open,
  onClose,
  onFillAnother,
  hasMoreWaivers,
  token,
  countdownSeconds = 10,
}: SubmissionSuccessModalProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(countdownSeconds);

  useEffect(() => {
    if (!open) {
      setCountdown(countdownSeconds);
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onFillAnother();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, countdownSeconds, onFillAnother]);

  const handleFillAnother = () => {
    onFillAnother();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          textAlign: "center",
          p: 2,
        },
      }}
    >
      <DialogContent sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 64,
              color: "success.main",
              mb: 2,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 2,
          }}
        >
          {I18n.t("Submit.success.title")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {I18n.t("Submit.success.message")}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleFillAnother}
            sx={{
              minWidth: 200,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {I18n.t("Submit.success.fillAnother")}
          </Button>
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.grey[50],
            borderRadius: 1,
            border: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {I18n.t("Submit.success.redirecting")}{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1.1em",
              }}
            >
              {countdown}
            </Box>{" "}
            {I18n.t("Submit.success.seconds")}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
